import { useEffect, useRef, type ReactElement } from 'react';
import { container } from 'tsyringe';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import IntegrationsPageLayout from '@/features/integrations/IntegrationsPageLayout';
import { AiProvidersPageViewModel } from '@/features/integrations/AiProvidersPageViewModel';
import { IdentityProvidersPageViewModel } from '@/features/integrations/IdentityProvidersPageViewModel';
import {
    AWS_BEDROCK_PROVIDER_TYPE_ID,
    AWS_BEDROCK_BUILD_LAUNCH_URL_ROUTE,
    buildAwsBedrockLaunchUrl,
} from '@/features/integrations/wizard/AwsBedrockLaunchUrlBuilder';
import type { LaunchUrlResolver } from '@/features/integrations/wizard/ConnectionStepAuth';
import { RuntimeConfigToken, type RuntimeConfig } from '@/runtime-config/RuntimeConfig';

const resolveLaunchUrl: LaunchUrlResolver = (providerTypeId, route, config) => {
    if (providerTypeId !== AWS_BEDROCK_PROVIDER_TYPE_ID) return null;
    if (route !== AWS_BEDROCK_BUILD_LAUNCH_URL_ROUTE) return null;
    const runtimeConfig = container.resolve<RuntimeConfig>(RuntimeConfigToken);
    const bedrock = runtimeConfig.integrations.awsBedrock;
    const externalId = typeof config['externalId'] === 'string' ? config['externalId'] : '';
    return buildAwsBedrockLaunchUrl({
        cfnTemplateUrl: bedrock.cfnTemplateUrl,
        platformAccountId: bedrock.platformAccountId,
        cloudFormationStackName: bedrock.cloudFormationStackName,
        consoleHostTemplate: bedrock.consoleHostTemplate,
        externalId,
    });
};

const COPY = {
    ai: {
        title: 'Connect a provider',
        subtitle: 'Pick one AI provider to connect — we pull usage from it to build your insights.',
        addLabel: 'Choose a provider',
        emptyTitle: 'No provider connected yet',
        emptyDescription: 'Connect a provider like OpenAI, Anthropic, or Bedrock to get started.',
    },
    directory: {
        title: 'Connect an identity provider',
        subtitle: 'Optional: connect a directory (Google, Entra, Okta) so usage maps to people and teams.',
        addLabel: 'Choose an identity provider',
        emptyTitle: 'No identity provider connected',
        emptyDescription: 'Connect a directory to sync your users and groups.',
    },
} as const;

/**
 * A self-contained "pick one and connect" step: embeds the inline provider
 * connection UI (no navigation away), auto-opening the picker when nothing is
 * connected yet, and reports the connected id up so the wizard can advance and
 * feed it to the first-run pipeline.
 */
export function OnboardingConnectStep({
    kind,
    onConnectedChange,
}: {
    kind: 'ai' | 'directory';
    onConnectedChange: (connectionId: string | null) => void;
}): ReactElement {
    const vm = useViewModel(kind === 'ai' ? AiProvidersPageViewModel : IdentityProvidersPageViewModel);
    const autoOpened = useRef(false);

    useEffect(() => { void vm.load(); }, [vm]);

    useEffect(() => {
        if (!autoOpened.current && !vm.loading && !vm.modalOpen && vm.connections.length === 0 && vm.providerTypes.length > 0) {
            autoOpened.current = true;
            vm.openCreate();
        }
    });

    const connectionId = vm.connections[0]?.id ?? null;
    useEffect(() => { onConnectedChange(connectionId); }, [connectionId, onConnectedChange]);

    const copy = COPY[kind];
    return (
        <IntegrationsPageLayout
            vm={vm}
            title={copy.title}
            subtitle={copy.subtitle}
            addLabel={copy.addLabel}
            emptyTitle={copy.emptyTitle}
            emptyDescription={copy.emptyDescription}
            {...(kind === 'ai' ? { onResolveLaunchUrl: resolveLaunchUrl } : {})}
        />
    );
}
